/*
 * Hook: 获取 Knative 工作负载数据
 */

import { useState, useEffect, useCallback } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes-react';
import {
  PodStatus,
  EventInfo,
  KnativeServiceStatus,
  KnativeRevisionStatus,
  KnativeRouteStatus,
  KnativeResourceOverview,
  KnativeCondition,
  KnativeTrafficTarget,
  ResourceMetrics,
} from '../types';

interface UseKnativeWorkloadsResult {
  service: KnativeServiceStatus | null;
  revisions: KnativeRevisionStatus[];
  route: KnativeRouteStatus | null;
  pods: PodStatus[];
  events: EventInfo[];
  overview: KnativeResourceOverview;
  metrics: ResourceMetrics | null;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

/**
 * 计算时间差
 */
function getAge(timestamp: string): string {
  const now = new Date();
  const created = new Date(timestamp);
  const diffMs = now.getTime() - created.getTime();
  
  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${seconds}s`;
}

/**
 * 解析 Knative Condition
 */
function parseConditions(conditions: any[]): KnativeCondition[] {
  return (conditions || []).map((c: any) => ({
    type: c.type,
    status: c.status,
    reason: c.reason,
    message: c.message,
    lastTransitionTime: c.lastTransitionTime,
  }));
}

/**
 * 解析 Knative Traffic
 */
function parseTraffic(traffic: any[]): KnativeTrafficTarget[] {
  return (traffic || []).map((t: any) => ({
    revisionName: t.revisionName,
    latestRevision: t.latestRevision,
    percent: t.percent || 0,
    tag: t.tag,
    url: t.url,
  }));
}

/**
 * 解析 Knative Service 状态
 */
function parseKnativeService(ksvc: any): KnativeServiceStatus {
  const status = ksvc.status || {};
  const spec = ksvc.spec || {};
  
  return {
    name: ksvc.metadata.name,
    namespace: ksvc.metadata.namespace,
    url: status.url,
    latestReadyRevision: status.latestReadyRevisionName,
    latestCreatedRevision: status.latestCreatedRevisionName,
    traffic: parseTraffic(status.traffic),
    conditions: parseConditions(status.conditions),
    age: getAge(ksvc.metadata.creationTimestamp),
    generation: ksvc.metadata.generation || 1,
    observedGeneration: status.observedGeneration,
  };
}

/**
 * 解析 Knative Revision 状态
 */
function parseKnativeRevision(
  revision: any, 
  serviceName: string,
  trafficMap: Map<string, number>,
  latestRevision?: string
): KnativeRevisionStatus {
  const status = revision.status || {};
  const spec = revision.spec || {};
  
  // 获取镜像
  const containers = spec.containers || [];
  const image = containers[0]?.image || '-';
  
  // 获取副本数
  const replicas = status.actualReplicas || 0;
  const readyReplicas = status.actualReplicas || 0; // Knative 自动扩缩容
  
  return {
    name: revision.metadata.name,
    namespace: revision.metadata.namespace,
    serviceName,
    generation: revision.metadata.labels?.['serving.knative.dev/configurationGeneration'] 
      ? parseInt(revision.metadata.labels['serving.knative.dev/configurationGeneration'], 10) 
      : 1,
    image,
    replicas,
    readyReplicas,
    conditions: parseConditions(status.conditions),
    age: getAge(revision.metadata.creationTimestamp),
    trafficPercent: trafficMap.get(revision.metadata.name),
    isLatest: revision.metadata.name === latestRevision,
  };
}

/**
 * 解析 Knative Route 状态
 */
function parseKnativeRoute(route: any): KnativeRouteStatus {
  const status = route.status || {};
  
  return {
    name: route.metadata.name,
    namespace: route.metadata.namespace,
    url: status.url,
    traffic: parseTraffic(status.traffic),
    conditions: parseConditions(status.conditions),
    age: getAge(route.metadata.creationTimestamp),
  };
}

/**
 * 解析 Pod 状态
 */
function parsePodStatus(pod: any): PodStatus {
  const phase = pod.status?.phase || 'Unknown';
  const containerStatuses = pod.status?.containerStatuses || [];
  
  const readyContainers = containerStatuses.filter((c: any) => c.ready).length;
  const totalContainers = containerStatuses.length || pod.spec?.containers?.length || 0;
  
  const restarts = containerStatuses.reduce(
    (sum: number, c: any) => sum + (c.restartCount || 0),
    0
  );
  
  let status: PodStatus['status'] = 'Unknown';
  if (phase === 'Running' && readyContainers === totalContainers) {
    status = 'Running';
  } else if (phase === 'Pending') {
    status = 'Pending';
  } else if (phase === 'Failed' || phase === 'Error') {
    status = 'Failed';
  } else if (phase === 'Succeeded') {
    status = 'Succeeded';
  }
  
  // 从 spec 中获取原始镜像（带 tag），status 中可能只有 digest
  const specContainers = pod.spec?.containers || [];
  const specImageMap = new Map<string, string>();
  specContainers.forEach((sc: any) => {
    if (sc.name && sc.image) specImageMap.set(sc.name, sc.image);
  });

  return {
    name: pod.metadata.name,
    namespace: pod.metadata.namespace,
    status,
    ready: `${readyContainers}/${totalContainers}`,
    restarts,
    age: getAge(pod.metadata.creationTimestamp),
    node: pod.spec?.nodeName || '-',
    ip: pod.status?.podIP,
    containers: containerStatuses.map((c: any) => ({
      name: c.name,
      image: specImageMap.get(c.name) || c.image,
      ready: c.ready,
      restartCount: c.restartCount || 0,
      state: c.state?.running ? 'running' : c.state?.waiting ? 'waiting' : 'terminated',
      reason: c.state?.waiting?.reason || c.state?.terminated?.reason,
    })),
  };
}

/**
 * 解析 Event 信息
 */
function parseEventInfo(event: any): EventInfo {
  return {
    type: event.type || 'Normal',
    reason: event.reason || '-',
    message: event.message || '-',
    involvedObject: {
      kind: event.involvedObject?.kind || '-',
      name: event.involvedObject?.name || '-',
    },
    count: event.count || 1,
    firstTimestamp: event.firstTimestamp || event.metadata?.creationTimestamp,
    lastTimestamp: event.lastTimestamp || event.metadata?.creationTimestamp,
    age: getAge(event.lastTimestamp || event.metadata?.creationTimestamp),
  };
}

/**
 * 获取 Knative 工作负载数据
 */
export function useKnativeWorkloads(
  clusterName: string,
  namespace: string,
  serviceName: string
): UseKnativeWorkloadsResult {
  const kubernetesApi = useApi(kubernetesApiRef);
  
  const [service, setService] = useState<KnativeServiceStatus | null>(null);
  const [revisions, setRevisions] = useState<KnativeRevisionStatus[]>([]);
  const [route, setRoute] = useState<KnativeRouteStatus | null>(null);
  const [pods, setPods] = useState<PodStatus[]>([]);
  const [events, setEvents] = useState<EventInfo[]>([]);
  const [metrics, setMetrics] = useState<ResourceMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  const fetchData = useCallback(async () => {
    if (!clusterName || !namespace || !serviceName) {
      setError(new Error('Missing cluster name, namespace, or service name'));
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // 并行获取 Knative 资源
      const [ksvcRes, revisionsRes, routeRes, podsRes, eventsRes] = await Promise.all([
        // Knative Service
        kubernetesApi.proxy({
          clusterName,
          path: `/apis/serving.knative.dev/v1/namespaces/${namespace}/services/${serviceName}`,
        }),
        // Knative Revisions (按 service 过滤)
        kubernetesApi.proxy({
          clusterName,
          path: `/apis/serving.knative.dev/v1/namespaces/${namespace}/revisions?labelSelector=serving.knative.dev/service=${serviceName}`,
        }),
        // Knative Route
        kubernetesApi.proxy({
          clusterName,
          path: `/apis/serving.knative.dev/v1/namespaces/${namespace}/routes/${serviceName}`,
        }),
        // Pods (按 Knative service label 过滤)
        kubernetesApi.proxy({
          clusterName,
          path: `/api/v1/namespaces/${namespace}/pods?labelSelector=serving.knative.dev/service=${serviceName}`,
        }),
        // Events
        kubernetesApi.proxy({
          clusterName,
          path: `/api/v1/namespaces/${namespace}/events?limit=50`,
        }),
      ]);
      
      // 解析响应
      const ksvcData = await ksvcRes.json();
      const revisionsData = await revisionsRes.json();
      const routeData = await routeRes.json();
      const podsData = await podsRes.json();
      const eventsData = await eventsRes.json();
      
      // 解析 Knative Service
      let parsedService: KnativeServiceStatus | null = null;
      if (ksvcData && !ksvcData.code) {
        parsedService = parseKnativeService(ksvcData);
        setService(parsedService);
      }
      
      // 构建 traffic map
      const trafficMap = new Map<string, number>();
      if (parsedService) {
        parsedService.traffic.forEach(t => {
          if (t.revisionName) {
            trafficMap.set(t.revisionName, t.percent);
          }
        });
      }
      
      // 解析 Revisions
      const parsedRevisions = (revisionsData.items || [])
        .map((rev: any) => parseKnativeRevision(
          rev, 
          serviceName, 
          trafficMap,
          parsedService?.latestReadyRevision
        ))
        .sort((a: KnativeRevisionStatus, b: KnativeRevisionStatus) => b.generation - a.generation);
      setRevisions(parsedRevisions);
      
      // 解析 Route
      if (routeData && !routeData.code) {
        setRoute(parseKnativeRoute(routeData));
      }
      
      // 解析 Pods
      const parsedPods = (podsData.items || []).map(parsePodStatus);
      setPods(parsedPods);
      
      // 解析 Events (过滤与 Knative 相关的)
      const knativeRelatedKinds = ['Service', 'Revision', 'Route', 'Configuration', 'PodAutoscaler', 'Pod'];
      const parsedEvents = (eventsData.items || [])
        .filter((e: any) => {
          const kind = e.involvedObject?.kind;
          const name = e.involvedObject?.name || '';
          return knativeRelatedKinds.includes(kind) && 
                 (name === serviceName || name.startsWith(`${serviceName}-`));
        })
        .map(parseEventInfo)
        .sort((a: EventInfo, b: EventInfo) => 
          new Date(b.lastTimestamp).getTime() - new Date(a.lastTimestamp).getTime()
        )
        .slice(0, 20);
      setEvents(parsedEvents);
      
      // 计算资源使用指标（简化版）
      const totalCpuRequests = parsedPods.reduce((sum: number, _pod: PodStatus) => sum + 100, 0);
      const totalMemRequests = parsedPods.reduce((sum: number, _pod: PodStatus) => sum + 128 * 1024 * 1024, 0);
      
      if (parsedPods.length > 0) {
        setMetrics({
          cpu: {
            usage: totalCpuRequests * 0.65,
            requests: totalCpuRequests,
            limits: totalCpuRequests * 2,
            percentage: 65,
          },
          memory: {
            usage: totalMemRequests * 0.45,
            requests: totalMemRequests,
            limits: totalMemRequests * 2,
            percentage: 45,
          },
        });
      } else {
        setMetrics(null);
      }
      
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [kubernetesApi, clusterName, namespace, serviceName, refreshKey]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const refresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);
  
  // 计算概览统计
  const overview: KnativeResourceOverview = {
    service: {
      name: service?.name || serviceName,
      ready: service?.conditions?.find(c => c.type === 'Ready')?.status === 'True',
      url: service?.url,
    },
    revisions: {
      total: revisions.length,
      ready: revisions.filter(r => r.conditions?.find(c => c.type === 'Ready')?.status === 'True').length,
      active: revisions.filter(r => (r.trafficPercent || 0) > 0).length,
    },
    pods: {
      total: pods.length,
      running: pods.filter(p => p.status === 'Running').length,
      pending: pods.filter(p => p.status === 'Pending').length,
      failed: pods.filter(p => p.status === 'Failed').length,
    },
    traffic: service?.traffic || [],
  };
  
  return {
    service,
    revisions,
    route,
    pods,
    events,
    overview,
    metrics,
    loading,
    error,
    refresh,
  };
}

